<template>
  <div id="incident-report-wrapper" data-test="outside-vif">
    <template v-if="hasErrors">
      <div :key="hasErrors" class="messages error messages--error">
        <b>Please correct the following error(s):</b>
        <ul>
          <li v-for="(error, index) in getErrors" :key="'error-' + index">
            {{ error }}
          </li>
        </ul>
      </div>
    </template>
    <div v-html="introMsg" />
    <hr />
    <p>Form fields that are required are marked with an asterisk (*).</p>
    <b-form class="raft-b4">
      <template v-for="field in shownFields">
        <single-select-filter
          v-if="field.field_name === 'field_in_report_building'"
          :key="field.field_name"
          :get-value="filteredBuildings"
          id="building-fil"
          data-test="building-filter"
          :multiple="false"
          label="Building"
          store-watch="getSelectedBuildingsFilter"
          display-value="title"
          track-by="uuid"
          @update-filter="
            (payload) => updateSelectedBuildings(payload.itemsSelected)
          "
          class="incident-report-location-field"
        />

        <single-select-filter
          v-else-if="field.field_name === 'field_in_report_space_room'"
          :key="field.field_name"
          :get-value="buildingSpaces"
          id="space-fil"
          data-test="space-filter"
          :multiple="false"
          label="Space/Room"
          display-value="title"
          store-watch="getSelectedSpacesFilter"
          :update-method="updateSelectedSpace"
          track-by="uuid"
          :disable="spaceFilterDisabled"
          class="incident-report-location-field"
        />

        <raft-file-upload
          v-else-if="field.field_name === 'field_in_report_upload_photos'"
          :key="field.field_name"
          :field-name="field.field_name"
          :label="field.label"
          data-test="upload-photos"
          :description="field.description"
          :field-config="field"
          :required="field.required"
          :multiple="true"
          :disable-removals="submitDisabled"
          @fileUploaded="handleFileSelectionAction($event)"
          suppress-replace-warning
        />

        <entity-form-field
          v-else-if="!skipField(field)"
          :key="field.field_name"
          :field-data="field"
          :disabled="field.disabled"
          entity-type="node"
          bundle="incident_report"
          @change="updateFormData"
          @update-form-data="updateFormData"
          @date-error-message="catchDateError"
          :data-test="field.field_name"
          class="form-control form-control-warning"
          :class="isInvalid(field.field_name)"
          :placeholder="entityFormHelper.getPlaceholder(field)"
          :add-class="addClass(field.field_name)"
          :styled="isStyled[field.field_name]"
        />
      </template>

      <b-form-row>
        <b-button
          type="submit"
          variant="primary"
          :disabled="submitDisabled"
          @click.prevent="handleSubmitAction()"
        >
          Submit
        </b-button>
        <b-link
          href="#"
          :disabled="submitDisabled"
          @click.prevent="handleCancelAction()"
        >
          Cancel
        </b-link>
        <div class="busy-wrapper">
          <raft-busy v-if="isBusy" :busy-text="busyText" />
        </div>
      </b-form-row>
    </b-form>
  </div>
</template>

<script>
/* eslint no-restricted-syntax: ["ForInStatement"] */
import { mapActions, mapGetters, mapMutations } from "vuex";
import axios from "axios";
import { restClient, jsonClient } from "../../lib/raftApi";
import EntityFormField from "../EntityFormField.vue";
import entityFormHelper from "../../lib/EntityFormHelper";
import RaftBusy from "../RaftBusy.vue";
import RaftFileUpload from "../raft/RaftFileUpload.vue";
import SingleSelectFilter from "../filters/SingleSelectFilter.vue";

export default {
  name: "InitialReportForm",
  components: {
    EntityFormField,
    RaftBusy,
    RaftFileUpload,
    SingleSelectFilter,
  },
  data() {
    return {
      errors: {},
      isBusy: false,
      spaceFilterDisabled: true,
      submitDisabled: false,
      busyText: "",
      imConfig: {},
      fields: null,
      formData: {},
      allBuildings: null,
      buildingSpaces: null,
      entityFormHelper: entityFormHelper,
      selectedFile: null,
      isStyled: {
        field_in_report_type_of_report: true,
        field_inc_report_injury_illness: true,
        field_inc_report_prop_dmg_loss: true,
        field_in_report_anon_submission: true,
      },
    };
  },
  computed: {
    ...mapGetters(["config"]),
    hasErrors() {
      return this.getErrors?.length;
    },
    introMsg() {
      return this.imConfig?.intro_msg;
    },
    filteredBuildings() {
      let buildings = this.allBuildings || [];
      const campus = this.formData?.field_in_report_campus;
      if (campus) {
        buildings = buildings.filter(
          (element) => element.parent_location_uuid === campus,
        );
      }
      return buildings;
    },
    shownFields() {
      if (typeof this.fields === "object" && this.fields !== null) {
        return this.fields;
      }
      return null;
    },
    getErrors: function () {
      const allErrors = [];
      const fields = Object.keys(this.errors);
      for (let i = 0; i <= fields.length; i++) {
        if (typeof this.errors[fields[i]] !== "undefined") {
          for (let j = 0; j < this.errors[fields[i]].length; j++) {
            allErrors.push(this.errors[fields[i]][j]);
          }
        }
      }
      return allErrors;
    },
  },
  methods: {
    ...mapMutations(["setSelectedSpacesFilter"]),
    ...mapActions(["updateSelectedBuildings"]),
    addClass(name) {
      if (
        name === "field_inc_report_people_involved" ||
        name === "field_inc_report_witnesses" ||
        name === "field_in_report_loc_specifics"
      ) {
        return "fullWidth";
      }
      return "";
    },
    async fetchImConfig() {
      this.imConfig = await restClient
        .get({
          path: "/incident-management-config",
          queries: {},
        })
        .catch((e) => {
          this.$showNotification("error", e.message);
        });
    },
    async getFilterItemsAsync() {
      try {
        const response = await restClient.get({
          path: "/buildings",
          queries: {},
        });
        this.allBuildings = response?.buildings;
      } catch (e) {
        this.$showNotification(
          "error",
          "Failed to fetch the list of buildings.",
        );
      }
    },
    isInvalid: function (fieldName) {
      return this.errors.hasOwnProperty(fieldName) ? "is-invalid" : null;
    },
    skipField: function (field) {
      return (
        field.field_name === "field_in_report_campus" &&
        entityFormHelper.getOptions(field).length < 1
      );
    },
    checkRequiredFields() {
      this.errors = [];
      this.fields.forEach((fieldConfig) => {
        if (
          fieldConfig.required &&
          !this.formData.hasOwnProperty(fieldConfig.field_name)
        ) {
          const message = 'The field "' + fieldConfig.label + '" is required.';
          this.errors[fieldConfig.field_name] = [message];
        }
      });
      return Object.keys(this.errors).length === 0;
    },
    catchDateError(error) {
      this.$emit("form-error-message", error);
      this.$showNotification("error", "The date selected is not available.");
    },
    errorEmit(message, data) {
      this.isBusy = false;
      this.submitDisabled = false;
      this.$showNotification("error", `${message}.`);
      this.$emit("form-error-message", { message: message, data: data });
    },
    handleFileSelectionAction(data) {
      this.selectedFile = data.field_value;
      this.updateFormData(data);
    },
    handleCancelAction: function () {
      this.submitDisabled = true;
      this.busyText = "Canceling...";
      this.isBusy = true;
      window.location.href = "/";
    },
    fetchFields: async function () {
      const options = {
        path: "/fields/node/incident_report",
        queries: {},
      };
      this.fields = await restClient
        .get(options)
        .catch((e) => this.errorEmit(e));
      const bundleFieldsSorted = [];
      if (this.fields) {
        Object.values(this.fields).forEach((fieldData) => {
          if (
            fieldData.hasOwnProperty("form_displays") &&
            typeof fieldData.form_displays.default !== "undefined"
          ) {
            if (fieldData.field_name === "field_in_report_type_of_report") {
              fieldData.form_display.third_party_settings = {
                bioraft_core_components: {
                  widget_options: {
                    buttons: true,
                  },
                },
              };
              fieldData.form_display.type = "options_buttons";
            }
            if (fieldData.field_name !== "revision_log") {
              bundleFieldsSorted.push(fieldData);
            }
          }
        });
        bundleFieldsSorted.sort(
          (a, b) =>
            a.form_displays.default.weight - b.form_displays.default.weight,
        );
        this.fields = bundleFieldsSorted;
      }
    },
    handleResponse(response) {
      this.isBusy = false;
      if (response?.data?.hasOwnProperty("id")) {
        if (!this.config.isB4) {
          axios
            .get(
              this.config.b3baseUrl +
                "/submit/incident-management-created-message/" +
                response.data.attributes.drupal_internal__nid,
              {},
            )
            .then(() => {
              window.location.href =
                "/raft/incident-management/incident-report/" +
                response.data.id +
                "/view";
            });
        } else {
          window.location.href =
            "/raft/incident-management/incident-report/" +
            response.data.id +
            "/view";
        }
      }
    },
    handleSubmitAction: function () {
      this.submitDisabled = true;
      this.busyText = "Submitting your incident report...";
      this.isBusy = true;

      if (this.checkRequiredFields()) {
        if (this.selectedFile) {
          this.uploadDocumentFiles();
        } else {
          this.submitForm();
        }
      } else {
        this.$showNotification("error", "Please fill out all required fields.");
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        this.submitDisabled = false;
        this.isBusy = false;
      }
    },
    setPhoneNumberState(bool) {
      const index = this.fields.findIndex(
        (i) => i.field_name === "field_inc_report_reporter_phone",
      );
      this.$set(this.fields, index, { ...this.fields[index], disabled: bool });
      if (!bool) {
        this.formData.field_inc_report_reporter_phone = null;
      }
    },
    uploadDocumentFiles() {
      const url =
        this.config.baseUrl +
        "/jsonapi/node/incident_report/field_in_report_upload_photos";
      const headers = {
        "Content-Type": "application/octet-stream",
        Authorization: "Bearer " + this.config.jwt,
        crossDomain: true,
        "X-CSRF-Token": this.config.csrfToken,
        "Content-Disposition": 'file; filename="filename.jpg"',
      };

      const promises = [];

      for (const file of this.selectedFile) {
        file.name
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/"/g, "&quot;");
        headers["Content-Disposition"] = 'file; filename="' + file.name + '"';
        promises.push(axios.post(url, file, { headers: headers }));
      }
      const uploadedFiles = {
        field_name: "field_in_report_upload_photos",
        field_value: [],
      };
      axios.all(promises).then(
        (results) => {
          results.forEach((response) => {
            uploadedFiles.field_value.push({
              type: "file--file",
              id: response.data.data.id,
            });
          });
          this.updateFormData(uploadedFiles);
          this.submitForm();
        },
        () => {
          this.submitDisabled = false;
          this.isBusy = false;
          const message = "Failed to upload the selected document.";
          this.errors.field_inc_invest_documents = [message];
          this.$showNotification("error", message);
        },
      );
    },
    async submitForm() {
      const data = entityFormHelper.createJsonApiEntityData(
        "node",
        "incident_report",
      );
      entityFormHelper.fillJsonApiDataFromFormData(
        data,
        this.fields,
        this.formData,
      );
      data.attributes.title = "Incident Report";
      const path = "node/incident_report";
      const options = {
        path,
        queries: {},
        data,
      };
      const response = await jsonClient
        .post(options)
        .catch((e) => this.errorEmit(e));
      return this.handleResponse(response);
    },
    updateSelectedSpace(newSpace) {
      this.setSelectedSpacesFilter(newSpace);
      if (!newSpace || newSpace.length === 0) {
        newSpace = null;
      }
      this.updateFormData({
        field_name: "field_in_report_space_room",
        field_value: newSpace,
      });
    },
    updateFormData(event) {
      if (event.field_name === "field_inc_report_incident_date") {
        const dateValue = new Date(event.field_value);
        const offset = dateValue.getTimezoneOffset();
        event.field_value = new Date(dateValue.getTime() - offset * 60 * 1000)
          .toISOString()
          .split("T")[0];
      }
      if (event.field_name === "field_in_report_anon_submission") {
        this.setPhoneNumberState(event.field_value);
      }
      if (event.field_name === "field_in_report_campus") {
        this.updateSelectedBuildings(null);
      }
      this.$set(this.formData, event.field_name, event.field_value);
    },
  },
  async created() {
    await Promise.all([
      this.fetchImConfig(),
      this.getFilterItemsAsync(),
      this.fetchFields(),
    ]);
    this.$store.watch(
      (state, getters) => getters.getSelectedBuildingsFilter,
      async (newBuilding) => {
        this.formData.field_in_report_space_room = null;
        this.buildingSpaces = null;

        if (!newBuilding || newBuilding.length === 0) {
          this.formData.field_in_report_building = null;
          this.spaceFilterDisabled = true;
          return;
        }

        const options = {
          path: "/spaces",
          queries: {
            parent_location: [newBuilding?.[0]?.uuid],
          },
        };
        const response = await restClient
          .get(options)
          .catch((e) => this.errorEmit(e));
        this.buildingSpaces = response.spaces;
        this.spaceFilterDisabled = false;
        this.updateFormData({
          field_name: "field_in_report_building",
          field_value: newBuilding?.[0]?.uuid,
        });
      },
    );
  },
};
</script>

<style scoped>
#raft-js #incident-report-wrapper input.form-control {
  width: 50%;
}

#raft-js div.incident-report-location-field {
  width: 47%;
  float: left;
  display: inline-block;
  margin-bottom: 12px;
}

/* Needed to clear the float above for building and space for the next form item */
#raft-js div.form-item {
  clear: both;
}
</style>
